import classNames from 'classnames';
import { InputHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Input from './Input';
import { DateRange, DayPicker, getDefaultClassNames } from 'react-day-picker';

import './DatePicker.css';

import { format } from 'date-fns';
import { CalendarDaysIcon, ClockIcon } from 'lucide-react';
import { getCurrentCopenhagenTime } from 'utils/dateTime';

interface DateTimePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  className?: string;
  inputClassName?: string;
  error?: string | boolean;
  value: DateRange | undefined;
  label?: string | React.ReactElement;
  onChange?: (newDate: DateRange | undefined, name: string) => void;
}

const dateFormatter = Intl.DateTimeFormat('en', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
});

function DateTimePickerRange({
  className,
  value,
  onChange,
  onBlur,
  name,
  ...rest
}: DateTimePickerProps) {
  const defaultClassNames = getDefaultClassNames();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const datePickerRef = useRef<HTMLDivElement | null>(null); // Reference for DayPicker container
  const datePickerInputRef = useRef<HTMLLabelElement | null>(null); // Reference for DayPicker input

  const [selectedMonth, setSelectedMonth] = useState(value?.from ?? new Date());

  const [selectedFromTime, setSelectedFromTime] = useState<string>(
    value?.from ? format(value.from, 'HH:mm') : getCurrentCopenhagenTime()
  );
  const [selectedToTime, setSelectedToTime] = useState<string>(
    value?.to ? format(value.to, 'HH:mm') : getCurrentCopenhagenTime()
  );

  const handleDayPickerSelect = (
    newDate: DateRange | undefined,
    newFromTime?: string,
    newToTime?: string
  ) => {
    if (onChange) {
      if (newDate?.from) {
        const ts = newFromTime ?? selectedFromTime;
        newDate.from = new Date(format(newDate.from, 'yyyy-MM-dd') + `T${ts}Z`);
      }
      if (newDate?.to) {
        const ts = newToTime ?? selectedToTime;
        newDate.to = new Date(format(newDate.to, 'yyyy-MM-dd') + `T${ts}Z`);
      }
      onChange(newDate, name ?? '');
    }
  };

  const handleFromTimeChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newFromTime = e.target.value;
    setSelectedFromTime(newFromTime);
    if (value?.from) {
      handleDayPickerSelect(
        {
          from: new Date(format(value.from, 'yyyy-MM-dd') + `T${newFromTime}Z`),
          to: value?.to,
        },
        newFromTime,
        selectedToTime
      );
    }
  };

  const handleToTimeChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newToTime = e.target.value;
    setSelectedToTime(newToTime);
    if (value?.to) {
      handleDayPickerSelect(
        {
          from: value?.from,
          to: new Date(format(value.to, 'yyyy-MM-dd') + `T${newToTime}Z`),
        },
        selectedFromTime,
        newToTime
      );
    }
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        datePickerInputRef.current &&
        !datePickerRef.current.contains(event.target as Node) &&
        !datePickerInputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false); // Close the date picker if click is outside
        if (onBlur) {
          // Trigger onBlur event if provided
          onBlur(new Event('blur') as unknown as React.FocusEvent<HTMLInputElement>);
        }
      }
    },
    [onBlur]
  );

  // Handle clicking outside of the date picker to close it
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mouseup', handleClickOutside);
    } else {
      document.removeEventListener('mouseup', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  const formattedValue = useMemo(() => {
    if (!value || !value.from) {
      return '';
    }

    const v: string = value?.from
      ? // @ts-expect-error typescript isn't up to date with date formatRange
        dateFormatter.formatRange(value.from, value?.to ?? value.from)
      : '';
    return v;
  }, [value]);

  return (
    <div className={classNames('relative', className)} data-component="DateTimePickerRange">
      <Input
        labelIcon={
          <span className="text-heading inline-block">
            <CalendarDaysIcon size={16} />
          </span>
        }
        onFocus={() => {
          setIsOpen(true);
        }}
        autoComplete="off"
        labelRef={datePickerInputRef}
        value={formattedValue}
        onChange={() => {
          if (onChange) {
            handleDayPickerSelect(undefined);
          }
        }}
        readOnly
        {...rest}
      />
      {isOpen && (
        <div
          ref={datePickerRef}
          className="absolute z-10 rounded-md border-2 border-neutral-500 bg-white px-3"
        >
          <DayPicker
            timeZone="UTC"
            captionLayout="dropdown"
            mode="range"
            selected={{
              from: value?.from ? new Date(value.from) : undefined,
              to: value?.to ? new Date(value.to) : undefined,
            }}
            onMonthChange={(newMonth) => {
              setSelectedMonth(newMonth);
            }}
            fixedWeeks
            weekStartsOn={1}
            onSelect={(newDate: DateRange | undefined) =>
              handleDayPickerSelect(newDate, selectedFromTime, selectedToTime)
            }
            showOutsideDays
            required={false}
            month={selectedMonth}
            classNames={{
              // day: 'rounded-md',
              range_start: 'range-start bg-blue-600 text-white rounded-l-md', // Highlight the start of the range
              range_end: 'range-end bg-blue-600 text-white rounded-r-md', // Highlight the end of the range
              range_middle: `bg-blue-100 rounded-none text-neutral-800`, // Highlight the range of selected days
              today: `rounded-md bg-blue-50 underline`, // Add a border to today's date
              selected: ``, // Highlight the selected day
              // root: `${defaultClassNames.root} shadow-lg p-5`, // Add a shadow to the root element
              chevron: `fill-blue-600`, // Change the color of the chevron
              // month: 'text-heading',
              button_previous: 'text-green-800 fill-red-800',
              caption_label: `${defaultClassNames.caption_label} text-neutral-800`,
            }}
          />

          <div className="mt-1 mb-2 flex items-center justify-around gap-4 border-t border-neutral-200 pt-3 text-neutral-800">
            <label className="flex items-center gap-2 text-sm font-semibold">
              <ClockIcon size={16} className="text-neutral-800" />
              Start
              <input
                type="time"
                value={selectedFromTime}
                onChange={handleFromTimeChange}
                className="h-9 rounded border border-neutral-500 bg-white px-2 font-normal text-neutral-800 outline-offset-1 outline-none focus:outline-4 focus:outline-blue-200"
              />
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <ClockIcon size={16} className="text-neutral-800" />
              End
              <input
                type="time"
                value={selectedToTime}
                onChange={handleToTimeChange}
                className="h-9 rounded border border-neutral-500 bg-white px-2 font-normal text-neutral-800 outline-offset-1 outline-none focus:outline-4 focus:outline-blue-200"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateTimePickerRange;
